// The build tag joins the bar here (docs/features/update-chip.md): a small group before ⋯ in #top — this page's build
// on facecheck / a preview / a local copy (tag.js says where), and inside Cody's Android app the phone's own chip
// (`window.CodyPhoneBuild`, the phone's PhoneBuildBridge.kt: {op:'watch'} → its chip on every change; {op:'tap'}).
// index.html is not touched: the group and its style are made here.
//
// One seam: `startBuildTag({ stamp, check })` → `{ offer(on, apply) }`. `check(why)` resolves 'updating' or 'current';
// `offer` is the offline layer's "a new version waits" (true: the tag shows it — the bar stays hidden).
import { tagShown, tagView, phoneView, narrowPick, tagHTML } from './tag.js'

const CSS = `
#build-tags { display: flex; align-items: center; gap: 6px; flex: none; }
#build-tags[hidden] { display: none; }
#build-tags .pill { font: 600 11px/1 ui-monospace, Menlo, monospace; letter-spacing: .02em; padding: 7px 9px; }
#build-tags .pill.accent { background: var(--c-build-accent); color: var(--c-on-accent); font-family: -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif; }
#build-tags .pill[aria-busy="true"] { cursor: default; }
@media (max-width: 599.98px) {
  #build-tags .pill { font: 600 13px/1 -apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif; padding: 0 10px; min-height: var(--screen-chip, 32px); }
  #top:has(#build-tags:not([hidden])) .who { display: none; }   /* the icon's room goes to the one pill */
}
`

function styled(doc) {
  if (doc.getElementById('buildtag-style')) return
  const style = doc.createElement('style')
  style.id = 'buildtag-style'
  style.textContent = CSS
  doc.head.appendChild(style)
}

export function startBuildTag({ stamp = {}, check = async () => 'current', host = globalThis.location?.hostname ?? '', doc = globalThis.document,
  bridge = globalThis.CodyPhoneBuild, log = () => {}, later = (f, ms) => setTimeout(f, ms),
  narrow = () => !!globalThis.matchMedia?.('(max-width: 599.98px)')?.matches } = {}) {
  const shown = tagShown(host)
  const st = { phase: 'idle', apply: null, phone: null }
  let el = null

  function draw() {
    if (!doc?.getElementById) return
    const top = doc.getElementById('top')
    if (!top) return
    let tag = shown ? tagView({ stamp, phase: st.phase }) : null, phone = st.phone
    if (narrow()) [tag, phone] = narrowPick(tag, phone)   // a phone's bar: one pill, short words
    if (!el) {
      if (!tag && !phone) return
      styled(doc)
      el = doc.createElement('span')
      el.id = 'build-tags'
      const more = doc.getElementById('more')
      if (more && more.parentNode === top) top.insertBefore(el, more); else top.appendChild(el)
      el.addEventListener('click', (e) => { const b = e.target?.closest?.('button[data-act]'); if (b) tap(b.dataset.act) })
    }
    el.innerHTML = tagHTML(tag, phone)
    el.hidden = !tag && !phone
  }

  async function tap(act) {
    if (act === 'reload') { log('build tag: Reload'); st.apply?.(); return }
    if (act === 'phone') { bridge?.postMessage?.(JSON.stringify({ op: 'tap' })); return }
    if (act !== 'check') return
    st.phase = 'checking'; draw()
    let result = 'current'
    try { result = await check('the build tag') } catch {}
    if (st.phase !== 'checking') return   // an offer arrived meanwhile: the tag says Reload
    st.phase = result === 'updating' ? 'updating' : 'current'; draw()
    // "Up to date" for a moment; "Updating…" until the offer comes (or a minute: a fetch that never finished)
    const was = st.phase
    later(() => { if (st.phase === was) { st.phase = 'idle'; draw() } }, was === 'current' ? 2500 : 60000)
  }

  // the phone app's chip (its canary / dev APK): what it says, on every change
  if (bridge?.postMessage && bridge?.addEventListener) {
    bridge.addEventListener('message', (e) => phoneSays(e?.data))
    try { bridge.postMessage(JSON.stringify({ op: 'watch' })) } catch {}
  }
  function phoneSays(message) { st.phone = phoneView(message); draw() }
  draw()
  // a phone turned, a window resized: one pill or two
  try { globalThis.matchMedia?.('(max-width: 599.98px)')?.addEventListener?.('change', draw) } catch {}

  return {
    /// "A new version waits" (on) or not: shown here → the tag says Reload and this returns true (the bar stays hidden).
    offer(on, apply) {
      if (!shown) return false
      st.apply = apply
      st.phase = on ? 'waiting' : (st.phase === 'waiting' ? 'idle' : st.phase)
      draw()
      return true
    },
    tap,
    /// what the phone app says (its bridge's message) — also how a test shows its chip
    phoneSays,
    get phase() { return st.phase },
    get phone() { return st.phone },
  }
}
